import { useTranslation } from "react-i18next";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { SectionReveal } from "@/components/shared/SectionReveal";
import { IMAGES } from "@/data/images";

export default function About() {
  const { t } = useTranslation();
  const bio = t("about.founder_bio", { returnObjects: true });
  const timeline = t("about.timeline", { returnObjects: true });
  const manifesto = t("about.manifesto", { returnObjects: true });

  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: t("about.founder_name"),
    jobTitle: "Founder & CEO, Fastority",
    worksFor: { "@type": "Organization", name: "Fastority" },
  };

  return (
    <div data-testid="about-page">
      <SEO title={`${t("about.hero_title")} — Fastority`} description={t("about.hero_subtitle")} schema={schema} path="about" />

      <section className="bg-deepblue text-white py-24 sm:py-32 grain-overlay relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,90,95,0.15),transparent_55%)]" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <RevealText lines={[t("about.hero_title")]} />
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">{t("about.hero_subtitle")}</p>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-offwhite">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-center gap-8 md:gap-12">
          <SectionReveal>
            <img src={IMAGES.logo} alt="Fastority" className="w-40 md:w-48 rounded-2xl mix-blend-multiply" data-testid="founder-logo" />
          </SectionReveal>
          <SectionReveal delay={0.1} className="space-y-5 md:flex-1">
            <h3 className="font-heading font-bold text-2xl text-slate900 text-center">{t("about.founder_name")}</h3>
            {bio.map((p, i) => (
              <p key={i} data-testid={`founder-bio-p-${i}`} className="text-lg text-slate-700 leading-relaxed text-justify">{p}</p>
            ))}
          </SectionReveal>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-16 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate900">{t("about.timeline_title")}</h2>
          </SectionReveal>
          <div className="relative border-l-2 border-deepblue/15 pl-8 space-y-12 ml-4">
            {timeline.map((step, i) => (
              <SectionReveal key={step.title} delay={i * 0.1}>
                <div data-testid={`timeline-step-${i}`} className="relative">
                  <span className="absolute -left-[42px] top-1 w-5 h-5 rounded-full bg-coral border-4 border-white shadow" />
                  <span className="text-xs uppercase tracking-[0.15em] font-bold text-coral">{step.year}</span>
                  <h3 className="font-heading font-bold text-xl text-slate900 mt-1">{step.title}</h3>
                  <p className="text-slate-600 mt-2 leading-relaxed max-w-xl">{step.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-slate900 text-white grain-overlay relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <SectionReveal className="mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">{t("about.manifesto_title")}</h2>
          </SectionReveal>
          <div className="space-y-0 divide-y divide-white/10">
            {manifesto.map((m, i) => (
              <SectionReveal key={m.number} delay={i * 0.08}>
                <div data-testid={`manifesto-item-${i}`} className="flex gap-8 py-8 items-start">
                  <span className="font-heading font-extrabold text-4xl sm:text-5xl text-white/20 shrink-0">{m.number}</span>
                  <div>
                    <h3 className="font-heading font-bold text-xl sm:text-2xl mb-2">{m.title}</h3>
                    <p className="text-white/60 leading-relaxed max-w-xl">{m.desc}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
